import { createContext, useContext, useEffect, useRef, useMemo } from 'react';
import type { ReactNode } from 'react';
import { toJSONSchema } from 'zod/v4/core';

import { allGlobalDefinitions } from './action-definitions';
import { EXECUTOR_SLOT } from './types';
import type { ActionDefinition, ActionResult, ExecuteCallback, TriggerCallback, ToolCatalogEntry } from './types';

type CapabilityParameter = {
  type: string;
  required: boolean;
  description: string;
  enumValues?: string[];
};

// Lossy conversion: the backend deserializes into a flat CapabilityParameter map.
// Ideally the v3api layer would accept JSON Schema directly (Anthropic messages api uses JSON Schema for Tool Input Schema)
// but for now we flatten to match the existing contract.
const zodToCapabilityParameters = (schema: ActionDefinition['parameters']): Record<string, CapabilityParameter> => {
  const jsonSchema = toJSONSchema(schema) as Record<string, unknown>;
  const properties = (jsonSchema.properties ?? {}) as Record<string, Record<string, unknown>>;
  const required = (jsonSchema.required ?? []) as string[];

  const result: Record<string, CapabilityParameter> = {};

  for (const [name, prop] of Object.entries(properties)) {
    const param: CapabilityParameter = {
      type: prop.enum ? 'enum' : ((prop.type as string) ?? 'string'),
      required: required.includes(name),
      description: (prop.description as string) ?? '',
    };

    if (prop.enum) {
      param.enumValues = prop.enum as string[];
    }

    result[name] = param;
  }

  return result;
};

type ViewContextGetter = () => Record<string, unknown>;

type LLMActionRegistryContextValue = {
  registerDefinition: (id: string, definition: ActionDefinition) => void;
  bindAction: (id: string, callback: ExecuteCallback | TriggerCallback, slot: string) => void;
  unbindAction: (id: string, slot: string) => void;
  getToolCatalog: () => ToolCatalogEntry[];
  executeAction: (id: string, params: Record<string, unknown>) => Promise<ActionResult>;
  triggerSlot: (id: string, slot: string) => Promise<ActionResult>;
  isDefinitionRegistered: (id: string) => boolean;
  isActionBound: (id: string, slot: string) => boolean;
  waitForBinding: (id: string, slot: string, timeoutMs?: number) => Promise<boolean>;
  setViewContext: (id: string, getter: ViewContextGetter) => void;
  clearViewContext: (id: string) => void;
  getViewContext: (id: string) => Record<string, unknown> | null;
};

const LLMActionRegistryContext = createContext<LLMActionRegistryContextValue | null>(null);

type LLMActionRegistryProviderProps = {
  children: ReactNode;
};

export const LLMActionRegistryProvider = ({ children }: LLMActionRegistryProviderProps) => {
  const definitionsRef = useRef<Map<string, ActionDefinition>>(new Map());
  const bindingsRef = useRef<Map<string, Map<string, ExecuteCallback | TriggerCallback>>>(new Map());
  const bindingListenersRef = useRef<Map<string, Map<string, Array<() => void>>>>(new Map());
  const viewContextRef = useRef<Map<string, ViewContextGetter>>(new Map());

  useEffect(() => {
    for (const { id, definition } of allGlobalDefinitions) {
      definitionsRef.current.set(id, definition);
    }
  }, []);

  const value = useMemo<LLMActionRegistryContextValue>(
    () => ({
      registerDefinition: (id, definition) => {
        definitionsRef.current.set(id, definition);
      },

      bindAction: (id, callback, slot) => {
        let slotMap = bindingsRef.current.get(id);
        if (!slotMap) {
          slotMap = new Map();
          bindingsRef.current.set(id, slotMap);
        }
        slotMap.set(slot, callback);

        const listenerMap = bindingListenersRef.current.get(id);
        const listeners = listenerMap?.get(slot);
        if (listeners) {
          listeners.forEach((resolve) => resolve());
          listenerMap!.delete(slot);
        }
      },

      unbindAction: (id, slot) => {
        const slotMap = bindingsRef.current.get(id);
        if (slotMap) {
          slotMap.delete(slot);
          if (slotMap.size === 0) {
            bindingsRef.current.delete(id);
          }
        }
      },

      getToolCatalog: () => {
        const catalog: ToolCatalogEntry[] = [];

        definitionsRef.current.forEach((definition, id) => {
          catalog.push({
            id,
            name: id,
            description: `[${definition.name}] ${definition.description}`,
            parameters: zodToCapabilityParameters(definition.parameters),
            category: definition.category,
          });
        });

        return catalog;
      },

      executeAction: async (id, params) => {
        const definition = definitionsRef.current.get(id);
        if (!definition) {
          return { success: false, error: `Action "${id}" is not defined` };
        }

        const slotMap = bindingsRef.current.get(id);
        const callback = slotMap?.get(EXECUTOR_SLOT) as ExecuteCallback | undefined;
        if (!callback) {
          return { success: false, error: `Action "${id}" has no executor bound — the required view may not be open` };
        }

        const parsed = definition.parameters.safeParse(params);
        if (!parsed.success) {
          return { success: false, error: `Invalid parameters for "${id}": ${parsed.error.message}` };
        }

        try {
          return await callback(parsed.data);
        } catch (error) {
          return { success: false, error: `Action "${id}" threw: ${String(error)}` };
        }
      },

      triggerSlot: async (id, slot) => {
        const slotMap = bindingsRef.current.get(id);
        const callback = slotMap?.get(slot) as TriggerCallback | undefined;
        if (!callback) {
          return { success: false, error: `Action "${id}" has no "${slot}" slot bound` };
        }

        try {
          return await callback();
        } catch (error) {
          return { success: false, error: `Action "${id}" slot "${slot}" threw: ${String(error)}` };
        }
      },

      isDefinitionRegistered: (id) => definitionsRef.current.has(id),

      isActionBound: (id, slot) => {
        const slotMap = bindingsRef.current.get(id);
        return slotMap?.has(slot) ?? false;
      },

      waitForBinding: (id, slot, timeoutMs = 10000) => {
        const slotMap = bindingsRef.current.get(id);
        if (slotMap?.has(slot)) return Promise.resolve(true);

        return new Promise<boolean>((resolve) => {
          const timeout = setTimeout(() => {
            const listenerMap = bindingListenersRef.current.get(id);
            const listeners = listenerMap?.get(slot);
            if (listeners) {
              listenerMap!.set(
                slot,
                listeners.filter((r) => r !== onBind),
              );
            }
            resolve(false);
          }, timeoutMs);

          const onBind = () => {
            clearTimeout(timeout);
            resolve(true);
          };

          let listenerMap = bindingListenersRef.current.get(id);
          if (!listenerMap) {
            listenerMap = new Map();
            bindingListenersRef.current.set(id, listenerMap);
          }
          const existing = listenerMap.get(slot) ?? [];
          existing.push(onBind);
          listenerMap.set(slot, existing);
        });
      },

      setViewContext: (id, getter) => {
        viewContextRef.current.set(id, getter);
      },

      clearViewContext: (id) => {
        viewContextRef.current.delete(id);
      },

      getViewContext: (id) => {
        const getter = viewContextRef.current.get(id);
        if (!getter) return null;
        return getter();
      },
    }),
    [],
  );

  return <LLMActionRegistryContext.Provider value={value}>{children}</LLMActionRegistryContext.Provider>;
};

export const useLLMActionRegistryContext = (): LLMActionRegistryContextValue => {
  const context = useContext(LLMActionRegistryContext);
  if (!context) {
    throw new Error('useLLMActionRegistryContext must be used within LLMActionRegistryProvider');
  }
  return context;
};
